/**
 * 总览 · 全站状态 (#2456): how the whole site stands right now. The route is
 * ungated (ADR-0028 decision 2); each card checks its own permission and is
 * not mounted — so sends no request — without it.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PageShell } from '@resource-kit/ui/react';
import { useToday } from '@resource-kit/controllers/react';
import ShellClock from '@components/layout/ShellClock';
import { useTenantBranding } from '@hooks/useTenantBranding';
import { usePermissions, useBetterAuth } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { formatBatchDay } from '@features/dispatch';
import { addCalendarDays } from '@lib/calendar-day';
import { headlineCounts } from '../overviewModel';
import { useOverviewBoard } from '../useOverviewBoard';
import { DeliveryDaysCard } from '../components/DeliveryDaysCard';
import { InboxCard } from '../components/InboxCard';
import { RecentOutcomesCard } from '../components/RecentOutcomesCard';
import { IntegrationsCard } from '../components/IntegrationsCard';
import { TomorrowDemandCard } from '../components/TomorrowDemandCard';

interface FrameProps {
  title: string;
  children: ReactNode;
}

/** Counts only; owner decision 2 keeps any dispatch time out of the title. */
function DispatchHeadlineFrame({ today, children }: { today: string; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { board } = useOverviewBoard(today);
  if (!board.data)
    return <OverviewFrame title={t('overview.headlineNoDispatch')}>{children}</OverviewFrame>;
  const counts = headlineCounts(board.data, today);
  const title = t('overview.headline', {
    delivered: counts.delivered,
    total: counts.total,
    tomorrow: formatBatchDay(counts.tomorrow, i18n.language),
    pending: counts.pending,
  });
  return <OverviewFrame title={title}>{children}</OverviewFrame>;
}

/** Section name, tenant (once loaded) and the merchant-time clock. */
function OverviewEyebrow() {
  const { t } = useTranslation();
  const { data } = useTenantBranding();
  return (
    <p data-testid="overview-eyebrow" className="text-xs text-muted-foreground">
      {[t('nav.overview'), data?.name].filter(Boolean).join(' · ')} · <ShellClock />
    </p>
  );
}

function OverviewFrame({ title, children }: FrameProps) {
  const { t } = useTranslation();
  return (
    <PageShell title={title} subtitle={t('overview.subtitle')} breadcrumb={<OverviewEyebrow />}>
      {children}
    </PageShell>
  );
}

export default function OverviewPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { isAdmin } = useBetterAuth();
  const today = useToday();
  const canDispatch = permissions.includes(PERMISSIONS.DISPATCH_READ);
  const canReview = permissions.includes(PERMISSIONS.REVIEW_READ);
  const canWebhooks = permissions.includes(PERMISSIONS.WEBHOOK_FAILURES_READ);

  const cards = (
    <div data-testid="overview-grid" className="grid grid-cols-1 gap-4 lg:grid-cols-12">
      {canDispatch ? <DeliveryDaysCard today={today} className="lg:col-span-7" /> : null}
      {canReview ? <InboxCard isAdmin={isAdmin} className="lg:col-span-5" /> : null}
      {canDispatch ? <RecentOutcomesCard today={today} className="lg:col-span-7" /> : null}
      {canWebhooks ? <IntegrationsCard isAdmin={isAdmin} className="lg:col-span-5" /> : null}
      {canDispatch ? (
        <TomorrowDemandCard tomorrow={addCalendarDays(today, 1)} className="lg:col-span-12" />
      ) : null}
    </div>
  );

  return canDispatch ? (
    <DispatchHeadlineFrame today={today}>{cards}</DispatchHeadlineFrame>
  ) : (
    <OverviewFrame title={t('overview.headlineNoDispatch')}>{cards}</OverviewFrame>
  );
}
